import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { API_URL } from './env';

export interface User {
  email: string;
  name?: string;
  /** Better Auth admin plugin role; 'admin' unlocks /admin. */
  role?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  async function checkSession() {
    try {
      const response = await fetch(`${API_URL}/v1/auth/get-session`, {
        credentials: 'include',
      });

      if (response.ok) {
        // Better Auth returns the JSON literal `null` when signed out.
        const data = await response.json();
        if (data?.session && data.user) {
          setUser(data.user);
        }
      }
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function signOut() {
    try {
      // Better Auth only accepts JSON; without it the request is rejected (415)
      // and the session stays valid on the server.
      const response = await fetch(`${API_URL}/v1/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!response.ok) throw new Error(`Sign out failed (${response.status})`);
    } catch (error) {
      console.error('Sign out error:', error);
      return; // keep the user signed in visibly rather than pretend
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
